/**
 * Reject Modal Component
 * 
 * Modal for rejecting disputes
 * Requires reason for rejection
 */

import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import BaseActionModal, { IActionModalProps, IModalChildProps } from './BaseActionModal';
import { ArbitrationAction } from '../../../services/arbitrationService';

interface RejectModalProps extends Omit<IActionModalProps, 'action'> {}

// Predefined rejection reasons
const REJECTION_REASONS = [
  { value: 'invalid_claim', label: 'ادعای نامعتبر' },
  { value: 'insufficient_evidence', label: 'مدارک کافی نیست' },
  { value: 'out_of_policy', label: 'خارج از سیاست‌های سایت' },
  { value: 'duplicate', label: 'درخواست تکراری' },
  { value: 'resolved_by_parties', label: 'توسط طرفین حل شده' },
  { value: 'other', label: 'سایر' }
];

const RejectModalContent: React.FC<IModalChildProps> = ({
  payload,
  setPayload,
  setIsValid
}) => {
  const [selectedReason, setSelectedReason] = React.useState('');
  const [customReason, setCustomReason] = React.useState('');

  useEffect(() => {
    const reason = selectedReason === 'other' 
      ? customReason 
      : REJECTION_REASONS.find(r => r.value === selectedReason)?.label || '';
    
    setPayload({ reason });
    setIsValid(reason.length >= 10);
  }, [selectedReason, customReason, setPayload, setIsValid]);

  return (
    <Box>
      <Alert severity="warning" icon={<CancelIcon />} sx={{ mb: 3 }}>
        <Typography variant="body2">
          با رد این اختلاف، کاربر اطلاع‌رسانی خواهد شد و امکان ثبت مجدد وجود نخواهد داشت.
        </Typography>
      </Alert>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>دلیل رد</InputLabel>
        <Select
          value={selectedReason}
          onChange={(e) => setSelectedReason(e.target.value)}
          label="دلیل رد"
        >
          {REJECTION_REASONS.map((reason) => (
            <MenuItem key={reason.value} value={reason.value}>
              {reason.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {selectedReason === 'other' && (
        <TextField
          fullWidth
          multiline
          rows={3}
          label="توضیحات دلیل رد"
          placeholder="لطفاً دلیل رد را با جزئیات بنویسید..."
          value={customReason}
          onChange={(e) => setCustomReason(e.target.value)}
          helperText={`${customReason.length}/10 حداقل کاراکتر`}
          error={customReason.length > 0 && customReason.length < 10}
        />
      )}

      {selectedReason && selectedReason !== 'other' && (
        <Box sx={{ 
          p: 2, 
          bgcolor: 'error.50', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'error.200'
        }}>
          <Typography variant="body2" color="error.dark">
            <strong>دلیل انتخاب شده:</strong> {REJECTION_REASONS.find(r => r.value === selectedReason)?.label}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const RejectModal: React.FC<RejectModalProps> = (props) => {
  return (
    <BaseActionModal {...props} action={ArbitrationAction.REJECT}>
      <RejectModalContent payload={{}} setPayload={() => {}} isValid={false} setIsValid={() => {}} />
    </BaseActionModal>
  );
};

export default RejectModal;

